import { useCallback, useEffect, useState } from "react";
import { Badge, BlockStack, Button, Card, IndexTable, InlineStack, Text, TextField } from "@shopify/polaris";
import { useOutletContext } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminShopifyAppInstallMapping } from "../../app/models/Admin";

import AdminMigrationDirectory from "./AdminMigrationDirectory";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const emptyInstallMapping: AdminShopifyAppInstallMapping = {
  shopDomain: "",
  appName: "",
  clientId: "",
  secretKey: "",
  appHandle: "",
};

export default function AdminStoresPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const [showInstallMappings, setShowInstallMappings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installMappings, setInstallMappings] = useState<AdminShopifyAppInstallMapping[]>([]);
  const [installMappingForm, setInstallMappingForm] = useState<AdminShopifyAppInstallMapping>(emptyInstallMapping);
  const [savingInstallMapping, setSavingInstallMapping] = useState(false);
  const [deletingInstallMappingId, setDeletingInstallMappingId] = useState<number | null>(null);

  const loadInstallMappings = useCallback(async () => {
    try {
      const response = await agent.Admin.shopifyAppInstallMappings(token);
      setInstallMappings(response);
    } catch {
      setError("Unable to load Shopify app install mappings.");
    }
  }, [token]);

  useEffect(() => {
    if (showInstallMappings) void loadInstallMappings();
  }, [loadInstallMappings, showInstallMappings]);

  const handleInstallMappingChange = <K extends keyof AdminShopifyAppInstallMapping>(key: K, value: AdminShopifyAppInstallMapping[K]) => {
    setInstallMappingForm((current) => ({ ...current, [key]: value }));
  };

  const handleEditInstallMapping = (mapping: AdminShopifyAppInstallMapping) => {
    setInstallMappingForm({
      ...mapping,
      secretKey: "",
    });
  };

  const handleSaveInstallMapping = async () => {
    setSavingInstallMapping(true);
    setError(null);

    try {
      await agent.Admin.upsertShopifyAppInstallMapping(token, installMappingForm);
      setInstallMappingForm(emptyInstallMapping);
      await loadInstallMappings();
    } catch {
      setError("Unable to save Shopify app install mapping.");
    } finally {
      setSavingInstallMapping(false);
    }
  };

  const handleDeleteInstallMapping = async (mapping: AdminShopifyAppInstallMapping) => {
    if (!mapping.id) return;

    const confirmed = window.confirm(`Delete install mapping for ${mapping.shopDomain}?`);
    if (!confirmed) return;

    setDeletingInstallMappingId(mapping.id);
    setError(null);

    try {
      await agent.Admin.deleteShopifyAppInstallMapping(token, mapping.id);
      await loadInstallMappings();
    } catch {
      setError("Unable to delete Shopify app install mapping.");
    } finally {
      setDeletingInstallMappingId(null);
    }
  };

  return (
    <BlockStack gap="400">
      <AdminMigrationDirectory token={token} onUnauthorized={onUnauthorized} />
      <Button onClick={() => setShowInstallMappings(value => !value)}>{showInstallMappings ? "Hide" : "Show"} advanced app-install mappings</Button>
      {showInstallMappings ? <Card>
        <BlockStack gap="300">
          {error ? <Text as="p" tone="critical">{error}</Text> : null}
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">Pre-install Shopify app mappings</Text>
            <Button onClick={loadInstallMappings}>Refresh mappings</Button>
          </InlineStack>
          <InlineStack gap="300" align="start">
            <div style={{ minWidth: "260px", flex: 1 }}>
              <TextField
                label="Shop domain"
                autoComplete="off"
                placeholder="example.myshopify.com"
                value={installMappingForm.shopDomain}
                onChange={(value) => handleInstallMappingChange("shopDomain", value)}
                disabled={savingInstallMapping}
              />
            </div>
            <div style={{ minWidth: "220px", flex: 1 }}>
              <TextField
                label="App name"
                autoComplete="off"
                value={installMappingForm.appName || ""}
                onChange={(value) => handleInstallMappingChange("appName", value)}
                disabled={savingInstallMapping}
              />
            </div>
            <div style={{ minWidth: "220px", flex: 1 }}>
              <TextField
                label="App handle"
                autoComplete="off"
                value={installMappingForm.appHandle || ""}
                onChange={(value) => handleInstallMappingChange("appHandle", value)}
                disabled={savingInstallMapping}
              />
            </div>
          </InlineStack>
          <InlineStack gap="300" align="start">
            <div style={{ minWidth: "320px", flex: 1 }}>
              <TextField
                label="Client ID / API key"
                autoComplete="off"
                value={installMappingForm.clientId}
                onChange={(value) => handleInstallMappingChange("clientId", value)}
                disabled={savingInstallMapping}
              />
            </div>
            <div style={{ minWidth: "320px", flex: 1 }}>
              <TextField
                label="API secret key"
                type="password"
                autoComplete="off"
                value={installMappingForm.secretKey || ""}
                onChange={(value) => handleInstallMappingChange("secretKey", value)}
                disabled={savingInstallMapping}
                helpText={installMappingForm.hasSecretKey ? "A secret is saved. Leave blank to keep it." : undefined}
              />
            </div>
          </InlineStack>
          <InlineStack gap="200" align="end">
            <Button onClick={() => setInstallMappingForm(emptyInstallMapping)} disabled={savingInstallMapping}>Clear form</Button>
            <Button variant="primary" onClick={handleSaveInstallMapping} loading={savingInstallMapping} disabled={savingInstallMapping}>
              Save install mapping
            </Button>
          </InlineStack>
          {installMappings.length === 0 ? (
            <Text as="p" tone="subdued">No pre-install mappings saved.</Text>
          ) : (
            <IndexTable
              resourceName={{ singular: "mapping", plural: "mappings" }}
              itemCount={installMappings.length}
              selectable={false}
              headings={[
                { title: "Shop domain" },
                { title: "App" },
                { title: "Client ID" },
                { title: "Secret" },
                { title: "Updated" },
                { title: "Actions" },
              ]}
            >
              {installMappings.map((mapping, index) => (
                <IndexTable.Row id={`${mapping.id}`} key={mapping.id || mapping.shopDomain} position={index}>
                  <IndexTable.Cell>{mapping.shopDomain}</IndexTable.Cell>
                  <IndexTable.Cell>{mapping.appName || mapping.appHandle || "-"}</IndexTable.Cell>
                  <IndexTable.Cell>{mapping.clientId}</IndexTable.Cell>
                  <IndexTable.Cell>{mapping.hasSecretKey ? <Badge tone="success">Saved</Badge> : <Badge tone="critical">Missing</Badge>}</IndexTable.Cell>
                  <IndexTable.Cell>{formatLocalDateTime(mapping.updatedAt || mapping.createdAt)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="200">
                      <Button variant="plain" onClick={() => handleEditInstallMapping(mapping)}>Edit</Button>
                      <Button
                        variant="plain"
                        tone="critical"
                        loading={deletingInstallMappingId === mapping.id}
                        disabled={deletingInstallMappingId !== null}
                        onClick={() => handleDeleteInstallMapping(mapping)}
                      >
                        Delete
                      </Button>
                    </InlineStack>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          )}
        </BlockStack>
      </Card> : null}
    </BlockStack>
  );
}
